import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const iso = (y, m, d) => new Date(Date.UTC(y, m, d)).toISOString().slice(0, 10);
const addDays = (s, n) => { const [y, m, d] = s.split("-").map(Number); return iso(y, m - 1, d + n); };

function monthDays(y, m) {
  const first = new Date(Date.UTC(y, m, 1));
  const offset = (first.getUTCDay() + 6) % 7; // lunes primero
  const n = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return [...Array(offset).fill(null), ...Array.from({ length: n }, (_, i) => iso(y, m, i + 1))];
}

// «octubre de 2026» → «Octubre de 2026»: solo la primera letra (la clase capitalize ponía «De»).
export function monthLabel(y, m, locale) {
  const s = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(y, m, 1)));
  return s.charAt(0).toLocaleUpperCase(locale) + s.slice(1);
}

// Calendario de rango por noches: la noche de cada día ocupado no se puede
// elegir; el día de devolución puede ser una noche ocupada (se devuelve por la mañana).
export default function RangeCalendar({ lang, busy, start, end, onChange, minDate }) {
  const base = new Date();
  const [offset, setOffset] = useState(0);
  const locale = lang === "en" ? "en-GB" : `${lang}-ES`;
  const months = useMemo(() => [0, 1].map((k) => {
    const d = new Date(Date.UTC(base.getFullYear(), base.getMonth() + offset + k, 1));
    return { y: d.getUTCFullYear(), m: d.getUTCMonth(), label: monthLabel(d.getUTCFullYear(), d.getUTCMonth(), locale) };
  }), [offset, locale]); // eslint-disable-line react-hooks/exhaustive-deps
  const weekdays = useMemo(() => Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(locale, { weekday: "narrow", timeZone: "UTC" }).format(new Date(Date.UTC(2024, 0, 1 + i)))), [locale]);

  const rangeFree = (a, b) => { for (let d = a; d < b; d = addDays(d, 1)) if (busy.has(d)) return false; return true; };

  const pick = (day) => {
    if (!start || end || day <= start) {
      if (busy.has(day)) return;
      return onChange({ start: day, end: "" });
    }
    if (!rangeFree(start, day)) return onChange({ start: busy.has(day) ? "" : day, end: "" });
    onChange({ start, end: day });
  };

  return (
    <div data-testid="range-calendar">
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={() => setOffset((o) => Math.max(0, o - 1))} className="p-1" data-testid="cal-prev-month"><ChevronLeft className="h-4 w-4" /></button>
        <button type="button" onClick={() => setOffset((o) => o + 1)} className="p-1" data-testid="cal-next-month"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        {months.map(({ y, m, label }) => (
          <div key={`${y}-${m}`}>
            <div className="text-center font-medium mb-2">{label}</div>
            <div className="grid grid-cols-7 gap-1 text-center text-xs text-[#8A8A8A] mb-1">{weekdays.map((w, i) => <div key={i}>{w}</div>)}</div>
            <div className="grid grid-cols-7 gap-1">
              {monthDays(y, m).map((day, i) => {
                if (!day) return <div key={`e${i}`} />;
                const past = day < minDate;
                const isBusy = busy.has(day);
                const selected = day === start || day === end;
                const inRange = start && end && day > start && day < end;
                const cls = past ? "text-[#C8C4BC]" : selected ? "bg-marca text-white" : inRange ? "bg-marca/15"
                  : isBusy ? "bg-[#EAE7E0] text-[#A8A49C] line-through" : "hover:bg-[#F2EFE8]";
                return (
                  <button key={day} type="button" disabled={past} onClick={() => pick(day)}
                    className={`h-9 rounded text-sm ${cls}`} data-testid={`day-${day}`}>
                    {Number(day.slice(8))}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
